import { debounce } from 'underscore';
import classname from 'classnames';
import React, { Component} from 'react';
import PropTypes from 'prop-types';
import { VirtualSessionIcon } from '@bizzabo/icons';

import FeatureToggleUtils from 'FeatureToggleUtils';
import BZPopover from 'web-common/bizzabo-components/components/BZPopover';

import LoginPopover from './LoginPopover';
import RegisterAndLoginPopper from './RegisterAndLoginPopper';
import ExternalUrlRedirectModal from './VirtualSessions/ExternalUrlRedirectModal';

import { getVirtualSessionEndPoint } from '../ApiUtil';
import {
    redirectToBroadcast,
    loadSessionPage,
    isMobile,
    buildNewrowUrl,
    getRestrictionReason,
    providerTypes
} from './sessionBroadcastUtils';
import NoAccessToVirtualSessionPopover from './NoAccessToVirtualSessionPopover';
import webcastingType from '../../../web-common/scripts/enums/webcastingTypeEnum';
import {
    sendCustomClick
} from '../../../utils/SnowplowUtils';
import {
    buttonTypes
} from '../../../constants/SnowplowConst';
import { isSnowplowTrackerEnabled } from '../../../utils/SnowplowUtils';

class JoinLiveSessionButton extends Component {
    state = {
        isBroadcastLoading: false,
        sessionAccessError: null,
        isRedirectModalVisible: false,
        externalSolutionUrl: '',
        externalSolutionType: '',
    };

    static propTypes = {
        siteConf: PropTypes.shape({}),
        event: PropTypes.object.isRequired,
        me: PropTypes.object.isRequired,
        session: PropTypes.object.isRequired,
        eventUrls: PropTypes.object.isRequired,
        showEmailLogin: PropTypes.func,
        setVirtualSessionDetails: PropTypes.func,
        setScrollToSessionBroadcastAction: PropTypes.func,
        setRecordUrl: PropTypes.func,
        showRegisterAndLoginModal: PropTypes.func.isRequired,
        ticketsSelectionData: PropTypes.object.isRequired,
    };

    sendVirtualSessionPostMessage = (virtualSessionDetailsObject) => {
        const {ticketsSelectionData = {}, me = {}, session} = this.props;
        const { primaryEmail } = me;
        const { tickets = [] } = ticketsSelectionData;
        const ticketIds = tickets.map( ticket => ticket.id);
        const postMessageData = {
            sessionId: session?.id,
            attendeeCastingUrl: virtualSessionDetailsObject?.attendeeUrl,
            attendeeEmail: primaryEmail,
            attendeeTickets: ticketIds,
            messageType: 'joinBroadcast'
        };
        window.parent.postMessage(postMessageData, '*');
    }

    onClickJoinBroadcastButton = async() => {
        const { session, event, setScrollToSessionBroadcastAction, virtualSessionDetails} = this.props;
        const sessionId = session.id;
        const isVirtualSessionEnabled = FeatureToggleUtils.isFeatureEnabled('feature-virtual-enable-virtual-suite');
        const isAvoidConcurrentUsersEnabled = !FeatureToggleUtils.isFeatureEnabled('avoidConcurrentUsersDisabled');

        let virtualSessionDetailsObject = null;
        if (virtualSessionDetails) {
            virtualSessionDetailsObject = virtualSessionDetails;
        } else {
            try {
                this.setState({isBroadcastLoading: true});
                virtualSessionDetailsObject = await getVirtualSessionEndPoint({sessionId, eventId: event.id});
                const {setVirtualSessionDetails} = this.props;
                setVirtualSessionDetails(sessionId, virtualSessionDetailsObject);
            } catch (error) {
                this.setState({sessionAccessError: getRestrictionReason({ reason: error })});
                return;
            } finally {
                this.setState({isBroadcastLoading: false});
            }
        }
        setScrollToSessionBroadcastAction && setScrollToSessionBroadcastAction(true);

        switch (virtualSessionDetailsObject.type){
        case webcastingType.NEWROW: {
            if (isMobile() && !(isAvoidConcurrentUsersEnabled || isSnowplowTrackerEnabled())){
                const newrowIframeSrcUrl = buildNewrowUrl(virtualSessionDetailsObject.token);
                redirectToBroadcast(newrowIframeSrcUrl);
            } else {
                loadSessionPage({event, sessionId});
            }
            break;
        }
        case webcastingType.WEBCASTING:
        case webcastingType.VIDEO_PROVIDER:
        case webcastingType.INTERACTIVE_VIDEO_PROVIDER:
        case webcastingType.EMBEDDED: {
            const requiresCspHeaders = virtualSessionDetailsObject?.providerType === providerTypes.HLS;
            loadSessionPage({event, sessionId, reloadCsp: requiresCspHeaders });
            break;
        }
        case webcastingType.EXTERNAL:
        case webcastingType.ZOOM: {
            const loadSessionPageForExternalSession = FeatureToggleUtils.isFeatureEnabled('external-session-join-leave');

            if (this.props.virtualSessionDetails && this.props.virtualSessionDetails.joinAction === 'NEW_TAB' || !loadSessionPageForExternalSession) {
                if (isVirtualSessionEnabled && virtualSessionDetailsObject?.joinAction === 'POST_MESSAGE') {
                    this.sendVirtualSessionPostMessage(virtualSessionDetailsObject);
                } else {
                    const redirectUrl = virtualSessionDetailsObject.type === webcastingType.ZOOM
                        ? virtualSessionDetailsObject?.url
                        : virtualSessionDetailsObject.attendeeUrl;
                    this.showRedirectModal(redirectUrl, virtualSessionDetailsObject.type);
                }
            } else {
                loadSessionPage({event, sessionId, reloadPage: true});
            }
            break;
        }
        case webcastingType.SIMULIVE: {
            const { setRecordUrl } = this.props;
            setRecordUrl(virtualSessionDetailsObject.url);
            loadSessionPage({event, sessionId});
            break;
        }
        }
    };

    showRedirectModal = (url, externalSolutionType = '') => {
        this.setState({ isRedirectModalVisible: true, externalSolutionUrl: url, externalSolutionType });
    };

    hideRedirectModal = () => {
        this.setState({ isRedirectModalVisible: false });
    };

    redirectToExternalLink = () => {
        const { session, event } = this.props;
        // send checkin when open external link
        getVirtualSessionEndPoint({ sessionId: session?.id, eventId: event?.id, shouldSendCheckin: true });
        window.open(this.state.externalSolutionUrl);
        this.hideRedirectModal();
    }

    getBroadcastBtnOverlay = ({
        btnText,
        clsBroadcastButton,
        clickCallback,
        isButtonDisabled,
        showIcon = true,
    }) => (
        <button
            className={clsBroadcastButton}
            onClick={(event) => {
                const { setLoginV2OffsetTop, siteConf: { isWidget }, analyticData } = this.props;
                sendCustomClick(buttonTypes.liveSessionButton, btnText, event.currentTarget);
                !isButtonDisabled && clickCallback && clickCallback(analyticData);
                if (isWidget && window.self !== window.parent) {
                    setLoginV2OffsetTop?.(event.pageY);
                }
            }}
        >
            {showIcon && (
                <VirtualSessionIcon
                    className="broadcast-icon"
                    fill="var(--page-contrast-to-main-color, #ffffff)"
                    fontSize="20px"
                />
            )}
            <span>{btnText}</span>
        </button>
    );

    render() {
        const {
            session,
            showEmailLogin,
            eventUrls,
            me,
            btnText = 'join now',
            showIcon = true,
            isDisabled,
            showRegisterAndLoginModal,
            siteConf,
            event,
            btnClass,
            analyticData,
        } = this.props;
        const enableVirtualSession = session.enableVirtualSession;

        if (!enableVirtualSession){
            return null;
        }

        const isAuthenticated = me && me.id;
        const { isBroadcastLoading, sessionAccessError, isRedirectModalVisible, externalSolutionType } = this.state;
        const isButtonHasDisabledView = isBroadcastLoading || isDisabled;
        const isButtonDisabled = isButtonHasDisabledView || !isAuthenticated;
        const isWidget = siteConf?.isWidget;
        const clsBroadcastButton = classname(`join-broadcast-btn bz-button font-size-12 custom-color color-1-background color-1-hover-opacity ${btnClass}`, {'broadcast-disabled': isButtonHasDisabledView });

        const clickCallback = debounce(this.onClickJoinBroadcastButton, 1000, true);
        const isAgendaLoginFlowEnabled = !FeatureToggleUtils.isFeatureEnabled('oldLogin');

        return (
            <>
                {
                    !isAuthenticated &&
                        (isAgendaLoginFlowEnabled ? (
                            (isWidget && window.self !== window.parent) ? (
                                <RegisterAndLoginPopper
                                    button={this.getBroadcastBtnOverlay({
                                        btnText,
                                        clsBroadcastButton,
                                        isButtonDisabled: false,
                                        showIcon
                                    })}
                                    onLoginClick={showEmailLogin}
                                    analyticData={analyticData}
                                    eventUrls={eventUrls}
                                    eventName={event.name}
                                />
                            ) : (
                                this.getBroadcastBtnOverlay({
                                    btnText,
                                    clsBroadcastButton,
                                    clickCallback: showRegisterAndLoginModal,
                                    isButtonDisabled: false,
                                    showIcon
                                })
                            )
                        ) : (
                            <BZPopover
                                placement="right"
                                cls="need-login-popover"
                                button={this.getBroadcastBtnOverlay({
                                    btnText,
                                    clsBroadcastButton,
                                    clickCallback,
                                    isButtonDisabled,
                                    showIcon
                                })}
                                popover={
                                    <LoginPopover
                                        analyticData={analyticData}
                                        onLoginClick={showEmailLogin}
                                        eventUrls={eventUrls}
                                    />
                                }
                            />
                        ))
                }
                {
                    isAuthenticated && !sessionAccessError &&
                    this.getBroadcastBtnOverlay({
                        btnText,
                        clsBroadcastButton,
                        clickCallback,
                        isButtonDisabled,
                        showIcon
                    })
                }
                {
                    sessionAccessError &&
                    <BZPopover
                        placement='right'
                        cls="no-access-to-session-popover"
                        defaultOverlayShown
                        onHide={() => this.setState({ sessionAccessError: null })}
                        button={this.getBroadcastBtnOverlay({
                            btnText,
                            clsBroadcastButton,
                            clickCallback,
                            isButtonDisabled,
                            showIcon
                        })}
                        popover={<NoAccessToVirtualSessionPopover sessionAccessError={sessionAccessError}/>}
                    />
                }
                <ExternalUrlRedirectModal
                    show={isRedirectModalVisible}
                    onHide={this.hideRedirectModal}
                    onContinue={this.redirectToExternalLink}
                    type={externalSolutionType}
                />
            </>
        );
    }
}

export default JoinLiveSessionButton;
